import { useCallback, useMemo } from 'react'
import { trackLink } from '@fs/zion-analytics'
import { useHistory, useLocation, useParams, useQueryParams } from '@fs/zion-router'
import { getBackTo, getNextTo } from '../lib/navigation'

// NOTE: Using a hook to handle browser navigation will make it easier to test
export default function useNavigate() {
  const history = useHistory()
  const location = useLocation()
  const { query } = useQueryParams() ?? {}
  const { memberUuid, mockMatchData, tryDemo } = query ?? {}
  const isTryDemo = useMemo(() => !!tryDemo, [tryDemo])
  const useMockMatchData = !!mockMatchData
  const { step } = useParams() // NOTE: Used for getNextTo and getBackTo logic

  const navigate = useCallback(
    (routerNavFunction, pathname, linkName, dropSearch, isExternal, newSearch) => {
      if (linkName && !isTryDemo)
        trackLink({ to: pathname, action: 'click_action', name: linkName, memberUUID: memberUuid })
      const pushObj = { pathname }
      if (newSearch) pushObj.search = newSearch
      else if (!dropSearch && location.search) pushObj.search = location.search

      if (isExternal) {
        let url = pathname
        if (pushObj.search) {
          url += pathname.includes('?') ? pushObj.search.replace('?', '&') : pushObj.search
        }
        window.location.assign(url)
      } else {
        routerNavFunction(pushObj)
        window.scrollTo(0, 0)
      }
    },
    [isTryDemo, memberUuid, location.search]
  )

  const onNewTab = useCallback(
    (pathname, linkName) => {
      if (!pathname) return
      if (linkName && !isTryDemo)
        trackLink({ to: pathname, action: 'click_action', name: linkName, memberUUID: memberUuid })
      window.open(pathname, '_blank')?.focus()
    },
    [isTryDemo, memberUuid]
  )

  const onPush = useCallback(
    (pathname, linkName, dropSearch, isExternal, newSearch) => {
      if (!pathname) return
      navigate(history.push, pathname, linkName, dropSearch, isExternal, newSearch)
    },
    [history.push, navigate]
  )

  const onReplace = useCallback(
    (pathname, linkName, dropSearch, isExternal, newSearch) => {
      if (!pathname) return
      navigate(history.replace, pathname, linkName, dropSearch, isExternal, newSearch)
    },
    [history.replace, navigate]
  )

  const reload = useCallback(() => window.location.reload(), [])

  const close = useCallback(() => window.close(), [])

  const getAddNameFlowBackTo = useCallback(
    (state, metadata) => getBackTo(step, state, metadata, isTryDemo),
    [step, isTryDemo]
  )

  const getAddNameFlowNextTo = useCallback(
    (state, metadata, ordinanceStatusReasons) => {
      return getNextTo(step, state, metadata, isTryDemo, ordinanceStatusReasons)
    },
    [step, isTryDemo]
  )

  return {
    close,
    getAddNameFlowBackTo,
    getAddNameFlowNextTo,
    location,
    onNewTab,
    onPush,
    onReplace,
    reload,
    isTryDemo,
    useMockMatchData,
  }
}
